import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import cn from "classnames";

// Mui Components
import { Box, Button, Collapse, Typography, TablePagination, IconButton, Grid, Link } from "@mui/material";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";

// Styles
import peopleStyles from "./People.styles";

// Components
import CustomIcon from "components/CustomIcon";
import TalentTable from "components/Talent/TalentTable";
import TalentTableToolbar from "components/Talent/TalentTable/TalentTableToolbar";
import AutocompleteField, { useAutocompletePeople } from "components/Autocomplete/AutocompleteField";
import ChipsArray from "components/Domain/ChipsArray";
import PeopleRoles from "./PeopleRoles";
import CountryCity from "../CountryCity";
import WHFilter, { WHData } from "components/Talent/WHFilter";
import NavButton from "components/HeaderNavigation/NavButton";
import { TalentHeader, TalentBody } from "../TalentLayoutContent";
import GenericError from "components/Errors/GenericError";
import JobTitle from "../JobTitle";
import LSPMFilter, { initNarrative } from "./LSPMFilter/LSPMFilter";
import TitleFilter from "../Companies/TitleFilter";
import { FieldHintButton } from "components/FieldHintButton";

// Hooks
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector, useAppDispatch } from "app/hooks";
import useTableActions from "components/Talent/TalentTable/useTableActions";
import useLeavePage from "utils/hooks/useLeavePage";

// Utils
import qs from "qs";
import { get, groupBy, intersection, isEmpty, orderBy, sortBy } from "lodash";
import { urlPaths } from "enums/urlPaths";
import { snakeCaseToCapitalizedString, sortWorkHistory, useRedirectOnProjectError } from "utils";
import {
    getNestedSortQueryParams,
    getPersonProp,
    getPeopleParams,
    talentPotentialMembersPayloadColumns,
    requestPeopleData,
} from "services/people/people.functions";
import {
    getCompanyListByPath,
    getPersonListByPath,
    getSearchAiRelatedCompanySetB,
    getSearchAiRelatedPersonSetA,
    getSearchAiRelatedSearchLeaders,
} from "pages/ProjectDashboard/adapters/formattedData";

// Services
import { getPeopleService } from "services/people/people.service";

// Store
import { showLoader, hideLoader } from "store/slice/UI";
import { IFilterPeople } from "store/slice/currentSearch";
import { getTalentSearchData } from "store/slice/talent";
import { getProject, getProjectById, resetProjectData } from "store/slice/Project";
import { getRoles, getRolesThunk } from "store/slice/Team/roles/rolesSlice";

// Others
import icons from "enums/icons";

// Constants
import { defaultLocationData, defaultCityRadius, defaultCountryData } from "../constants/talent.constants";
import { WHdata, WHRelevance } from "components/Talent/WHFilter";

// Types
import { TBasicFunctionItem } from "components/Talent/People/PeopleRoles/PeopleRoles.types";
import {
    ILocationSearch,
    ILocationCountry,
    ILocationCountryById,
    ILocationDataById,
    fetchCountryByIdService,
    fetchLocationByIdService,
    saveProjectByPathService,
    updateProjectByPathService,
} from "services";
import { PersonTalent, WorkHistoryData } from "services/people/people.types";
import { TAutocompleteFieldSelectedObjectValue } from "components/Autocomplete/AutocompleteField/AutocompleteField.types";
import {
    PeoplePayload,
    ColumnsPeople,
    TshowPopoverPeople,
    Order,
    apiSortIdValues,
    OrderTable,
} from "components/Talent/TalentTable/TalentTable.types";
import { ChipEntity } from "types";
import { TLSPMNarrativeBase } from "./LSPMFilter/LSPMFilter.types";

type Pagination = {
    page: number;
    rows: number;
    orderByColumn?: string;
};

export const initPersonInfo = { left: -1, top: -1, id: -1 };

export const convertFiltersForProjects = (filters: IFilterPeople) => {
    const selectedWHScope = filters.workHistory.find((wh) => wh.selected) as WHData;
    const WHScope = selectedWHScope?.label.split(".");

    return {
        work_history_scope: selectedWHScope ? WHScope[WHScope.length - 1].toUpperCase() : "ALL",
        role_ids: filters.selectedRoles.filter((role) => role.ids?.length).flatMap((role) => role.ids as number[]),
        job_title: filters.jobTitleTerm,
        geo_radius: {
            country_id: filters.selectedCountry.id,
            city_id: filters.selectedCity.id,
            radius: Number(filters.radius),
            location: {
                lat: filters.selectedCity.location?.latitude || null,
                lon: filters.selectedCity.location?.longitude || null,
            },
        },
        lspm_scores: filters.lspmNarratives
            .map((lspm) => {
                return {
                    narrative: lspm.id.toUpperCase(),
                    score_range: {
                        min: lspm?.checkboxes?.minimum ? lspm.min ?? null : null,
                        max: lspm?.checkboxes?.maximum ? lspm.max ?? null : null,
                    },
                };
            })
            .filter((lspm) => lspm.narrative.length),
    };
};

const People = () => {
    const intl = useIntl();
    const classes = peopleStyles();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError();

    const [isDumbSearch, setIsDumbSearch] = useState<boolean>(true);
    const [activeButton, setActiveButton] = useState<number>(0);

    const [collapse, setCollapse] = useState(true);
    const [showCollapseExpand, setShowCollapseExpand] = useState(false);
    const [showPopover, setShowPopover] = useState<TshowPopoverPeople>("none");

    const { data: rolesData, status: rolesStatus } = useAppSelector(getRoles);
    const talentSearch = useAppSelector(getTalentSearchData);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const potentialMembersDataFetched = useRef<boolean>(false);

    // Params
    const { search } = useLocation();
    const params = useMemo(() => qs.parse(search, { ignoreQueryPrefix: true }), [search]);

    // Project
    const { data: projectData, status: projectStatus } = useAppSelector(getProject);
    const [personListPath, setPersonListPath] = useState("");

    // Selected companies
    const [selectedCompanies, setSelectedCompanies] = useState<TAutocompleteFieldSelectedObjectValue[]>([]);

    // Roles
    const [roles, setRoles] = useState<TBasicFunctionItem[] | (TBasicFunctionItem & ChipEntity)[]>([]);
    const [groupedRoles, setGroupedRoles] = useState<(TBasicFunctionItem & ChipEntity)[]>([]);

    // Job titles
    const [jobTitleTerm, setJobTitleTerm] = useState("");

    // Cities and countries
    const [city, setCity] = useState<ILocationSearch>(defaultLocationData);
    const [radius, setRadius] = useState<string | number>(defaultCityRadius);
    const [country, setCountry] = useState<ILocationCountry>(defaultCountryData);

    // WH Filter
    const [whFilter, setWHFilter] = useState<WHData[]>(WHdata);

    // WH Relevance Filter
    const [whRelevanceFilter, setWHRelevanceFilter] = useState<WHData[]>(WHRelevance);

    // LSPM Filter
    const [narrativesArr, setNarrativesArr] = useState<TLSPMNarrativeBase[]>([initNarrative]);

    // Popover Person
    const personInfo = useRef({ ...initPersonInfo });

    // Table
    const [people, setPeople] = useState<PeoplePayload>({ meta: {}, results: [] });
    const [selectedPeople, setSelectedPeople] = useState<PersonTalent[]>([]);
    const [sortingDirection, setSortingDirection] = useState<OrderTable>("default");

    const [pagination, setPagination] = useState<Pagination>({ page: 0, rows: 100 });

    const [
        { markedLeft, markedRight },
        {
            setMarkedLeft,
            setMarkedRight,
            handleAddBulk,
            handleDeleteBulk,
            handleClickRow,
            handleAddSelectAllClick,
            handleDeleteSelectAllClick,
            handleAddRow,
            handleDeleteRow,
        },
    ] = useTableActions({ data: people.results, setSelectedPeople, type: "people", selectedPeople });

    const {
        peopleList,
        placeholderPerson,
        setPeopleList,
        getOptionLabelPerson,
        renderOptionPerson,
        handleInputChangePerson,
    } = useAutocompletePeople();

    const handleTabChange = (index: number) => {
        if (index !== activeButton) {
            setIsDumbSearch(!isDumbSearch);
            setActiveButton(index);
        }
    };

    const convertOrderValue = (orderByColumn: string) => {
        const order = orderByColumn.split(",");

        if (order[0].includes("work_history")) {
            return getNestedSortQueryParams(order[0], order[1], selectedCompanies);
        }
        if (order[0].includes("score")) {
            return {
                _score: { order: "desc" },
                "name.keyword_lc": { order: "asc" },
            };
        }
        return { [order[0]]: { order: order[1] } };
    };

    const sortByWorkHistoryProp = (peopleList: PersonTalent[], sortBy: string, field: keyof WorkHistoryData) => {
        const direction = sortBy.split(",")[1];
        return orderBy(
            peopleList,
            [
                function (person) {
                    return ((person.work_history[0] as WorkHistoryData)[field] as string).toLowerCase();
                },
            ],
            direction === "asc" ? ["asc", "desc"] : ["desc", "asc"]
        );
    };

    const reorderWorkHistory = (
        peopleList: PersonTalent[],
        paginationOptions?: { page: number; rows?: number; orderByColumn?: string }
    ) => {
        peopleList.forEach((person) => {
            const companies = selectedCompanies.map((company) => company.value);
            const sortedWorkHistory = sortWorkHistory(person.work_history, "tenure_to");

            const workHistoryWithSelectedCompany = sortedWorkHistory.reduce((acc, workHistory) => {
                const selectedWH = companies.includes((workHistory.company_name || workHistory.unlinked_company) ?? "")
                    ? workHistory
                    : ({} as WorkHistoryData);

                if (isEmpty(selectedWH)) {
                    return acc;
                } else {
                    return selectedWH.is_current_employment ? [selectedWH] : [...acc, selectedWH];
                }
            }, [] as WorkHistoryData[]);

            person.work_history = workHistoryWithSelectedCompany;
        });

        if (paginationOptions?.orderByColumn?.includes("company_name")) {
            return sortByWorkHistoryProp(peopleList, paginationOptions.orderByColumn, "company_name");
        }

        if (paginationOptions?.orderByColumn?.includes("drx_work_position_label")) {
            return sortByWorkHistoryProp(peopleList, paginationOptions.orderByColumn, "drx_work_position_label");
        }

        return peopleList;
    };

    const handlePeopleFetch = async (
        paginationOptions?: { page: number; rows?: number; orderByColumn?: string },
        dontShowLoader?: boolean
    ) => {
        try {
            !dontShowLoader && dispatch(showLoader(true));

            const convertedLspmOrderBy = narrativesArr.length
                ? narrativesArr?.map((element) => ({
                      field: element.id,
                      range: {
                          gte: element.min,
                          lte: element.max,
                      },
                  }))
                : [];

            const commonParams = {
                jobTitleTerm: jobTitleTerm,
                companies: selectedCompanies.filter((company) => company.selected).map(({ key }) => key),
                meta: {
                    page_no: (paginationOptions?.page ?? 0) + 1 || 1,
                    page_size: paginationOptions?.rows || pagination.rows,
                },
                sort: {
                    ...(paginationOptions?.orderByColumn
                        ? { ...convertOrderValue(paginationOptions.orderByColumn) }
                        : { _score: { order: "desc" }, "name.keyword": { order: "asc" } }),
                },
            };

            const params = getPeopleParams(
                isDumbSearch
                    ? {
                          ...commonParams,
                          workHistoryState: whRelevanceFilter.find((e) => e.selected)?.label,
                      }
                    : {
                          ...commonParams,
                          workHistoryState: whFilter.find((e) => e.selected)?.label,
                          roles: roles
                              .filter((role) => role?.ids !== undefined)
                              .flatMap((role) => role.ids as number[]),
                          location: {
                              ...(country.id && !city.id ? { countryId: country.id } : {}),
                              ...(city.id ? { city, location_radius: Number(radius) } : {}),
                          },
                          lspmNarratives: narrativesArr,
                          order_by: [
                              {
                                  operator: "SCORE_LSPM_HITS",
                                  operand: {
                                      ranges: [...(convertedLspmOrderBy as [])],
                                  },
                                  direction: "asc",
                              },
                              {
                                  field: "id",
                                  direction: "asc",
                              },
                          ],
                      }
            );

            const { data: dataResults } = await getPeopleService<PersonTalent>(params);

            setPeople({
                meta: {
                    results_total: dataResults.meta.hits,
                    page_total: dataResults.meta.pages,
                    page_size: dataResults.meta.page_size,
                    page: dataResults.meta.page_no,
                },
                results: isDumbSearch
                    ? reorderWorkHistory(dataResults.results, paginationOptions)
                    : dataResults.results,
            });

            setPagination((currentPagination) => ({ ...currentPagination, ...paginationOptions }));
        } catch (error) {
            console.error(error);
        } finally {
            !dontShowLoader && dispatch(hideLoader(false));
        }
    };

    const onRequestSort = async (orderByColumn: apiSortIdValues, direction: OrderTable, propertyId: string) => {
        const orderBy = direction === "default" ? "_score,desc" : `${orderByColumn},${direction as Order}`;
        setPagination((currentPagination) => ({ ...currentPagination, orderByColumn: orderBy }));
        handlePeopleFetch({ page: pagination.page || 0, rows: pagination.rows, orderByColumn: orderBy }, true);
    };

    const onSort = (property: ColumnsPeople, direction: Order, isNumeric: boolean) => {
        if (!selectedPeople.length) return;

        if (isNumeric) {
            const sortedPeople = [...selectedPeople].sort((personA, personB) => {
                const a = Number(get(personA, property));
                const b = Number(get(personB, property));
                return direction === "desc" ? a - b : b - a;
            });
            setSelectedPeople(sortedPeople);
        } else {
            const sortedPeople = [...selectedPeople].sort((personA, personB) => {
                const [path, prop] = property.split(".");
                const peopleArray = get(personA, path);
                let a, b;

                if (Array.isArray(peopleArray) && property.includes("work_history")) {
                    a = String(getPersonProp(personA, path, prop)).toLowerCase();
                    b = String(getPersonProp(personB, path, prop)).toLowerCase();
                } else {
                    a = String(get(personA, property) ?? "").toLowerCase();
                    b = String(get(personB, property) ?? "").toLowerCase();
                }
                if (a === b) return 0;
                else {
                    if (direction === "asc") return a < b ? -1 : 1;
                    else return a < b ? 1 : -1;
                }
            });
            setSelectedPeople(sortedPeople);
        }
    };

    const updateSearch = useCallback(async () => {
        try {
            dispatch(showLoader(true));
            await saveProjectByPathService({
                id: Number(params.projectId),
                project_path: `/project_${params.projectId}/search_ai_list/search_ai_${params.searchAiId}/search_leaders`,
                data: convertFiltersForProjects({
                    radius,
                    jobTitleTerm: jobTitleTerm,
                    selectedRoles: roles,
                    selectedCity: city,
                    selectedCountry: country,
                    workHistory: whFilter,
                    workHistoryRelevance: whRelevanceFilter,
                    lspmNarratives: narrativesArr,
                }),
            });
        } catch (error) {
            console.error(error);
        }
    }, [
        dispatch,
        params.projectId,
        params.searchAiId,
        radius,
        jobTitleTerm,
        roles,
        city,
        country,
        whFilter,
        whRelevanceFilter,
        narrativesArr,
    ]);

    useLeavePage(updateSearch);

    const handleGoBack = () => {
        navigate({
            pathname: urlPaths.Talent,
            search: `?projectId=${params.projectId}&insightId=${params.insightId}&searchAiId=${params.searchAiId}`,
        });
    };

    const handleSaveLeaders = async () => {
        try {
            dispatch(showLoader(true));
            if (personListPath) {
                await updateProjectByPathService({
                    id: Number(params.projectId),
                    project_path: personListPath,
                    data: { person_ids: selectedPeople.map((person) => person.id) },
                });
            } else {
                const { data } = await saveProjectByPathService({
                    id: Number(params.projectId),
                    project_path: `/project_${params.projectId}/person_set_list`,
                    data: {
                        name: `Long list ` + `${new Date().getTime()}`.slice(-5),
                        type: "LONG_LIST",
                        person_ids: selectedPeople.map((person) => person.id),
                    },
                });

                const peopleSetId = data.children[data.children.length - 1].data.id;

                await updateProjectByPathService({
                    id: Number(params.projectId),
                    project_path: `/project_${params.projectId}/search_ai_list/search_ai_${params.searchAiId}/long_list`,
                    data: {
                        data: {},
                        ref: {
                            path: `project_${params.projectId}/person_set_list/person_set_${peopleSetId}`,
                        },
                    },
                });
            }
        } catch (error) {
            dispatch(showLoader(false));
            console.error(error);
        }

        dispatch(resetProjectData());
        navigate(
            `${urlPaths.TalentRefinement}?projectId=${params.projectId}&insightId=${params.insightId}&searchAiId=${params.searchAiId}`
        );
    };

    const handleFilterCandidates = async () => {
        setPagination((currentPagination) => ({ ...currentPagination, page: 0 }));
        setPeople({ meta: {}, results: [] });
        setSortingDirection("default");
        await updateSearch();
        handlePeopleFetch();
        setCollapse(false);
    };

    const onPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setMarkedLeft([]);
        handlePeopleFetch({ page, orderByColumn: pagination?.orderByColumn });
    };

    const collapseHandler = () => {
        setCollapse(!collapse);
        if (!collapse) setShowCollapseExpand(false);
    };

    const collapseHandlerEnd = () => {
        if (!collapse) setShowCollapseExpand(true);
    };

    // Fetch Roles
    useEffect(() => {
        const fetchRolesData = async () => {
            try {
                await dispatch(getRolesThunk());
            } catch (error) {
                console.error(error);
            }
        };

        if (!rolesData.length && rolesStatus !== "fetching") {
            fetchRolesData();
        }

        if (rolesData.length) {
            const groupedByFunctions = groupBy(rolesData, "function");
            const functionsList = Object.entries(groupedByFunctions).map((element, index) => ({
                id: index,
                function: element[0],
                ids: element[1].map((element) => element.id),
                key: index,
                value: element[0],
                selected: false,
            }));

            setGroupedRoles(sortBy(functionsList, ["function"]) as (TBasicFunctionItem & ChipEntity)[]);
        }
    }, [dispatch, rolesData, rolesStatus]);

    // Fetch Potential members data
    // useEffect(() => {
    //     const getPotentialMembersData = async () => {
    //         const potentialMembersWithData = await getDataForPotentialMembers(talentSearch?.state?.data);
    //         potentialMembersWithData?.length && setSelectedPeople(potentialMembersWithData);
    //         potentialMembersDataFetched.current = true;
    //     };

    //     if (talentSearch?.state?.data?.potential_members?.length && !potentialMembersDataFetched.current) {
    //         getPotentialMembersData();
    //     }
    // }, [talentSearch]);

    // Fetch Project data
    useEffect(() => {
        const fetchData = async () => {
            if (params.projectId) {
                try {
                    dispatch(showLoader(true));
                    await dispatch(getProjectById({ projectId: Number(params.projectId) }));
                } catch (error) {
                    console.error(error);
                } finally {
                    dispatch(showLoader(false));
                }
            }
        };

        const fetchLocationData = async (locationData: any) => {
            try {
                if (locationData.country_id) {
                    const { data } = await fetchCountryByIdService<ILocationCountryById>(locationData.country_id);

                    data &&
                        setCountry({
                            id: data.country_id,
                            name: data.country_record.name,
                            code_iso_alpha2: data.country_record.code_iso_alpha2,
                            code_iso_alpha3: data.country_record.code_iso_alpha3,
                        });
                }

                if (locationData.city_id) {
                    const { data } = await fetchLocationByIdService<ILocationDataById>(locationData.city_id);

                    data &&
                        setCity({
                            id: data.location_id,
                            city: data.location_record.city,
                            country_id: data.location_record.country_id,
                            country: data.location_record.country,
                            location: data.location_record.location,
                        });
                }
            } catch (error) {
                console.error(error);
            }

            if (locationData.geo_radius?.radius) {
                setRadius(locationData.geo_radius.radius);
            }
        };

        const fetchLonglistData = async (peopleIds: number[]) => {
            const peopleWithData = await requestPeopleData(peopleIds, talentPotentialMembersPayloadColumns);
            setSelectedPeople(peopleWithData);
            potentialMembersDataFetched.current = true;
        };

        if (isEmpty(projectData)) {
            projectStatus !== "fetching" && fetchData();
        } else {
            if (!params.searchAiId && !params.projectId) return;

            // Selected companies
            const peersSetPath = getSearchAiRelatedCompanySetB(projectData, params.searchAiId)?.ref?.path;
            const peersList = getCompanyListByPath(projectData, peersSetPath)?.children?.map((c) => c.data);

            if (peersList) {
                setSelectedCompanies(
                    peersList.map((company) => ({
                        key: company.id,
                        value: company.name ?? "",
                        selected: true,
                    }))
                );
            }

            // Filters
            const filtersPeople = getSearchAiRelatedSearchLeaders(projectData, params.searchAiId)?.data;

            if (filtersPeople && !isEmpty(filtersPeople)) {
                // Roles (Function)
                if (filtersPeople.role_ids && rolesData.length) {
                    setRoles(
                        groupedRoles
                            .map((r) => {
                                return {
                                    ...r,
                                    selected: Boolean(intersection(r.ids, filtersPeople.role_ids).length),
                                };
                            })
                            .filter((r) => r.selected)
                    );
                }

                // Job Title
                if (filtersPeople.job_title) {
                    setJobTitleTerm(filtersPeople.job_title);
                }

                // Location
                if (filtersPeople.geo_radius && !isEmpty(filtersPeople.geo_radius)) {
                    fetchLocationData(filtersPeople.geo_radius);
                }

                // Work History Scope
                if (filtersPeople.work_history_scope) {
                    const advancedSearchWH = WHdata.map((wh) => ({
                        ...wh,
                        selected: wh.label.includes(filtersPeople.work_history_scope.toLowerCase()),
                    }));

                    const dumbSearchWH = WHRelevance.map((wh, i) => {
                        const isHistoric = filtersPeople.work_history_scope.toLowerCase() === "historic";
                        return {
                            ...wh,
                            selected:
                                i === 0 && isHistoric
                                    ? true
                                    : wh.label.includes(filtersPeople.work_history_scope.toLowerCase()),
                        };
                    });

                    setWHFilter(advancedSearchWH);
                    setWHRelevanceFilter(dumbSearchWH);
                }

                // Narratives
                if (filtersPeople.lspm_scores.length) {
                    setNarrativesArr(
                        (currentNarratives) =>
                            filtersPeople.lspm_scores.map((lc) => ({
                                id: lc.narrative.toLowerCase(),
                                label: snakeCaseToCapitalizedString(lc.narrative),
                                ...(lc.score_range.min ? { min: lc.score_range.min } : {}),
                                ...(lc.score_range.max ? { max: lc.score_range.max } : {}),
                                ...(lc.score_range.min !== null || lc.score_range.max !== null
                                    ? {
                                          checkboxes: {
                                              minimum: typeof lc.score_range.min === "number",
                                              maximum: typeof lc.score_range.max === "number",
                                          },
                                      }
                                    : {}),
                            })) ?? currentNarratives
                    );
                }
            }

            // Longlist
            const peopleSetPath = getSearchAiRelatedPersonSetA(projectData, params.searchAiId)?.ref?.path;
            const peopleSetList = getPersonListByPath(projectData, peopleSetPath)?.data.person_ids;

            peopleSetPath && setPersonListPath(peopleSetPath);
            peopleSetList?.length && fetchLonglistData(peopleSetList);
        }
    }, [dispatch, navigate, groupedRoles, params, projectData, projectStatus, rolesData, talentSearch]);

    return (
        <ErrorBoundary FallbackComponent={GenericError}>
            <Box ref={containerRef}>
                <TalentHeader
                    title={intl.formatMessage({ id: "talent.people.title" })}
                    actions={
                        <Box>
                            <NavButton isBackButton onClick={handleGoBack} translationId={"talent.companies.title"} />
                            <NavButton
                                translationId="button.analyse.candidates"
                                onClick={handleSaveLeaders}
                                disabled={!selectedPeople.length}
                            />
                        </Box>
                    }
                >
                    <Box className={classes.filtersWrapper}>
                        {!collapse && showCollapseExpand ? (
                            <IconButton className={classes.filtersExpanderButton} onClick={collapseHandler}>
                                <ExpandCircleDownIcon sx={{ color: "black" }} />
                            </IconButton>
                        ) : null}

                        {/* Filters */}
                        <Collapse in={collapse} className={classes.collapse} onExited={collapseHandlerEnd}>
                            <Typography paragraph className={classes.headerNarrative}>
                                {intl.formatMessage({ id: "talent.people.filters.headerNarrative" })}
                            </Typography>
                            <Box sx={{ mb: 4 }}>
                                <Box className={classes.wrapperSubtitleLinks}>
                                    <TitleFilter
                                        showCrossLine
                                        title={intl.formatMessage({ id: "talent.people.filters.subtitle" })}
                                        icon={<CustomIcon icon={icons.filter} />}
                                    />
                                    <Box sx={{ flex: 1, height: "1px", backgroundColor: "#DADEE6", margin: "auto" }} />
                                    <Box className={classes.tabsContainer}>
                                        <Link
                                            className={`${classes.tabButton} ${
                                                activeButton === 0 && classes.activeTabButton
                                            }`}
                                            onClick={() => handleTabChange(0)}
                                        >
                                            <Typography>
                                                {intl.formatMessage({ id: "talent.people.filters.basicSearch" })}
                                            </Typography>
                                        </Link>
                                        <Link
                                            className={`${classes.tabButton} ${
                                                activeButton === 1 && classes.activeTabButton
                                            }`}
                                            onClick={() => handleTabChange(1)}
                                        >
                                            <Typography>
                                                {intl.formatMessage({ id: "talent.people.filters.advancedSearch" })}
                                            </Typography>
                                        </Link>
                                    </Box>
                                </Box>

                                <Typography paragraph>
                                    {intl.formatMessage({ id: "talent.people.filters.subtitleNarrative" })}
                                </Typography>
                            </Box>

                            {/* Selected Companies */}
                            <Box className={classes.filterWrapperGreen}>
                                <Box className={classes.countryCityHeader}>
                                    <CustomIcon className={classes.labelIcon} icon={icons.peer} />
                                    <Typography className={classes.labelTitle}>
                                        {intl.formatMessage({ id: "talent.people.filters.companies" })}
                                    </Typography>
                                </Box>
                                <Typography paragraph className={classes.narratives}>
                                    {intl.formatMessage({ id: "talent.people.filters.companies.narrative" })}
                                </Typography>
                                <ChipsArray colorChip="secondary" chipData={selectedCompanies} />
                            </Box>

                            {/* WH Relevance */}
                            {isDumbSearch ? (
                                <Box className={classes.filterWrapperGrey}>
                                    <WHFilter
                                        data={whRelevanceFilter}
                                        setData={setWHRelevanceFilter}
                                        title={intl.formatMessage({ id: "talent.people.wh.relevance" })}
                                    />
                                </Box>
                            ) : null}

                            {/* WHFilter */}
                            {!isDumbSearch ? (
                                <Box className={classes.filterWrapperGrey}>
                                    <WHFilter
                                        data={whFilter}
                                        setData={setWHFilter}
                                        title={intl.formatMessage({ id: "talent.people.wh" })}
                                    />
                                </Box>
                            ) : null}

                            {/* Functions */}
                            {!isDumbSearch ? (
                                <Box className={classes.filterWrapperGrey}>
                                    <PeopleRoles rolesList={groupedRoles} roles={roles} setRoles={setRoles} />
                                </Box>
                            ) : null}

                            {/* Job Title */}
                            <Box className={classes.filterWrapperGrey}>
                                <JobTitle term={jobTitleTerm} setTerm={setJobTitleTerm} />
                            </Box>

                            {/* Country/City/Radius */}
                            {!isDumbSearch ? (
                                <Box className={classes.filterWrapperGrey}>
                                    <CountryCity
                                        city={city}
                                        radius={radius}
                                        country={country}
                                        setCity={setCity}
                                        setRadius={setRadius}
                                        setCountry={setCountry}
                                    />
                                </Box>
                            ) : null}

                            {/* LSPM filter */}
                            {!isDumbSearch ? (
                                <Box className={classes.filterWrapperGrey}>
                                    <LSPMFilter lspmNarratives={narrativesArr} setLspmNarratives={setNarrativesArr} />
                                </Box>
                            ) : null}

                            <Button
                                variant="contained"
                                className={classes.filterButton}
                                onClick={handleFilterCandidates}
                                startIcon={<CustomIcon icon={icons.filter} />}
                                disabled={jobTitleTerm ? jobTitleTerm.length < 3 : false}
                            >
                                {/* Filter Candidates */}
                                <Typography variant="body2">
                                    {intl.formatMessage({ id: "talent.people.filters.button" })}
                                </Typography>
                            </Button>
                            <IconButton className={classes.filtersExpanderButton} onClick={collapseHandler}>
                                <ExpandCircleDownIcon sx={{ transform: "rotate(180deg)", color: "black" }} />
                            </IconButton>
                        </Collapse>
                    </Box>
                </TalentHeader>

                <TalentBody>
                    <Grid container>
                        <Grid item className={classes.talentTableSource}>
                            <TalentTableToolbar
                                numResults={people.results.length}
                                type="people"
                                disabled={!Boolean(markedLeft.length)}
                                onMarked={handleAddBulk}
                                action="add"
                                isBasicSearch={isDumbSearch}
                                pagination={
                                    <TablePagination
                                        rowsPerPageOptions={[]}
                                        component="div"
                                        count={people.meta.results_total ?? 0}
                                        rowsPerPage={pagination.rows}
                                        page={pagination.page}
                                        onPageChange={onPageChange}
                                        showFirstButton
                                        showLastButton
                                    />
                                }
                            />
                        </Grid>
                        <Grid item className={classes.talentTableSelected}>
                            <TalentTableToolbar
                                unSelectables={selectedPeople.filter(
                                    (el) => el?.potential_member_data?.in !== undefined
                                )}
                                numResults={selectedPeople.filter((el) => !el?.potential_member_data?.in).length}
                                type="people"
                                disabled={!Boolean(markedRight.length)}
                                onMarked={handleDeleteBulk}
                                action="delete"
                                search={
                                    <AutocompleteField
                                        blurOnSelect
                                        inputSize="small"
                                        showStartAdorment
                                        renderCantFindData
                                        hideDefaultEndAdorment
                                        openOnlyOnOptionsLoaded
                                        showEndAdorment={false}
                                        options={peopleList}
                                        disableClearable={false}
                                        filterOptions={(x) => x}
                                        id="talent-search-company"
                                        placeholder={placeholderPerson}
                                        optionsUpdater={setPeopleList}
                                        renderOption={renderOptionPerson}
                                        getOptionLabel={getOptionLabelPerson}
                                        onInputChangeCallback={handleInputChangePerson}
                                        hintButton={
                                            <FieldHintButton>
                                                <FormattedMessage id="field.hint.people.search" />
                                            </FieldHintButton>
                                        }
                                        onOptionSelectCallback={(
                                            person: Pick<PersonTalent, "id" | "name"> | string | null
                                        ) => {
                                            if (person === null) return;

                                            const personFound = selectedPeople.find(
                                                ({ id }) => id === (person as PersonTalent).id
                                            );

                                            if (!personFound) {
                                                setSelectedPeople((currentPeople) => [
                                                    person as PersonTalent,
                                                    ...currentPeople,
                                                ]);
                                            }
                                        }}
                                    />
                                }
                            />
                        </Grid>
                        <Grid item className={cn(classes.talentTableSource, classes.talentTableSourceSx)}>
                            <TalentTable
                                type="people"
                                data={people.results}
                                selectedElements={selectedPeople}
                                setSelectedElements={setSelectedPeople}
                                setMarked={setMarkedLeft}
                                marked={markedLeft}
                                onClickRow={handleClickRow}
                                isBasicSearch={isDumbSearch}
                                isLongList={{
                                    value: false,
                                    onSelectAllAdd: handleAddSelectAllClick,
                                    onAddRow: handleAddRow,
                                }}
                                onRequestSort={onRequestSort}
                                containerRef={containerRef}
                                showPopover={showPopover}
                                setShowPopover={setShowPopover}
                                popoverPeople={personInfo}
                                sortingDirection={sortingDirection}
                                setSortingDirection={setSortingDirection}
                            />
                        </Grid>
                        <Grid item className={cn(classes.talentTableSelected, classes.talentTableSelectedSx)}>
                            <TalentTable
                                hideColumnsSubmenu
                                containsSelectOptions
                                type="people"
                                columns={["name"]}
                                data={selectedPeople}
                                selectedElements={selectedPeople}
                                setSelectedElements={setSelectedPeople}
                                setMarked={setMarkedRight}
                                marked={markedRight}
                                onClickRow={handleClickRow}
                                isLongList={{
                                    value: true,
                                    onSelectAllDelete: handleDeleteSelectAllClick,
                                    onDeleteRow: handleDeleteRow,
                                }}
                                onSort={onSort}
                                containerRef={containerRef}
                                showPopover={showPopover}
                                setShowPopover={setShowPopover}
                                popoverPeople={personInfo}
                            />
                        </Grid>
                    </Grid>
                </TalentBody>
            </Box>
        </ErrorBoundary>
    );
};

export default People;
